import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { xor } from 'lodash';
import { IngredientModal, useIngredientModal } from '@web/components';
import { ProductSelectionForm } from '../ProductSelectionForm';
import { ApiResult } from '../../api';
import { useReporter } from '../../reporter';
import {
  recipeSelected,
  recipeUnselected,
  recipesShown,
  userContinuesToCheckoutEvent,
  userViewFreshRecipeScreen,
  userExpandsFreshRecipeDetails,
} from './events';

export const FreshRecipesSelectionForm = ({
  onSubmit,
  headline,
  subheadline,
  modals,
  ctaText,
  checkoutBarHeadline,
  items: recipes,
  defaultRecipes,
  onBackButtonClick,
  unavailableRecipes,
  getConfigFromRecipes,
  period,
}) => {
  const viewPageTagFired = useRef(false);
  const recipesShownTagFired = useRef(false);
  const [selectedRecipes, setSelectedRecipes] = useState(defaultRecipes);
  const { price, discountedPrice, message } =
    getConfigFromRecipes(selectedRecipes);

  const { modal, onRecipeDetailsClick } = useIngredientModal(modals);

  const reporter = useReporter();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const availableRecipes = recipes.filter(
    ({ value }) => !unavailableRecipes.includes(value),
  );

  useEffect(() => {
    if (!viewPageTagFired.current) {
      reporter.tag(userViewFreshRecipeScreen(availableRecipes.length));
      viewPageTagFired.current = true;
    }
    if (!recipesShownTagFired.current) {
      const presentedRecipes = recipes.map(recipe => recipe.value);
      const unselectedRecipes = xor(defaultRecipes, presentedRecipes);
      reporter.tag(
        recipesShown(defaultRecipes, unselectedRecipes, unavailableRecipes),
      );
      recipesShownTagFired.current = true;
    }
  }, [
    availableRecipes.length,
    defaultRecipes,
    recipes,
    reporter,
    unavailableRecipes,
  ]);

  const onChange = recipes => {
    const currentRecipes = selectedRecipes ?? [];
    const recipeAdded = recipes.length > currentRecipes.length;
    const recipe = xor(recipes, currentRecipes)[0];
    const event = recipeAdded ? recipeSelected : recipeUnselected;
    reporter.tag(event(recipe));
    setSelectedRecipes(recipes);
  };

  const isValid = selectedRecipes && selectedRecipes.length > 0;
  const noSelectedRecipes = !selectedRecipes?.length;

  const onSubmitHandler = async event => {
    event.preventDefault();
    if (!isValid) {
      return;
    }
    setIsSubmitting(true);
    reporter.tag(userContinuesToCheckoutEvent(selectedRecipes));
    const submitResult = await onSubmit(selectedRecipes);
    ApiResult.ifError(submitResult, () => setIsSubmitting(false));
  };

  const sortedSelectedRecipes = noSelectedRecipes ? [] : selectedRecipes;

  const checkoutBarProps = {
    headline: checkoutBarHeadline,
    ctaText,
    isSubmitDisabled: !isValid || isSubmitting,
    isBackButtonDisabled: isSubmitting,
    shown: true,
    onBackButtonClick: onBackButtonClick,
    price,
    discountedPrice,
    message,
    period,
    isSubmitting,
  };

  const legend = `Choose up to ${
    recipes.length >= 4 ? '4' : recipes.length
  } recipe${recipes.length > 1 ? 's' : ''}`;

  const onRecipeDetailsHandler = product => {
    reporter.tag(userExpandsFreshRecipeDetails(product.value));
    onRecipeDetailsClick(product.value);
  };
  const productSelectorProps = {
    onDetailsClick: onRecipeDetailsHandler,
    products: recipes,
    selected: sortedSelectedRecipes,
    unavailableProducts: unavailableRecipes,
    period,
    buttonIcon: 'plus',
  };

  return (
    <>
      <ProductSelectionForm
        checkoutBar={checkoutBarProps}
        headline={headline}
        legend={legend}
        onChange={onChange}
        onSubmit={onSubmitHandler}
        productSelector={productSelectorProps}
        subheadline={subheadline}
      />
      <IngredientModal {...modal} />
    </>
  );
};
FreshRecipesSelectionForm.displayName = 'FreshRecipesSelectionForm';
FreshRecipesSelectionForm.propTypes = {
  modals: PropTypes.array.isRequired,
  onSubmit: PropTypes.func.isRequired,
  headline: PropTypes.string.isRequired,
  subheadline: PropTypes.node.isRequired,
  checkoutBarHeadline: PropTypes.node,
  ctaText: PropTypes.string.isRequired,
  defaultRecipes: PropTypes.array,
  unavailableRecipes: PropTypes.array,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      description: PropTypes.node.isRequired,
      img: PropTypes.any,
    }),
  ),
  onBackButtonClick: PropTypes.func.isRequired,
  getConfigFromRecipes: PropTypes.func.isRequired,
  period: PropTypes.string.isRequired,
};
